import {
  getCookieBool,
  setCookie,
  unsetCookie,
} from '@wandb/weave/common/util/cookie';
import {useEffect} from 'react';

import {Viewer} from '../state/viewer/types';
import {safeLocalStorage} from '../util/localStorage';
import {isInIframe} from '../util/window';

const ANON_SESSION_KEY = 'anon_session_id';

const generateSessionId = (length: number = 32): string => {
  const randomBytes = new Uint8Array(length);
  window.crypto.getRandomValues(randomBytes);
  // Convert bytes to hexadecimal strings
  return Array.from(randomBytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
};

// For weave-python to work with requests from embedded public reports, it must
// receive an `anon_session_id` that it uses as a cache key.
//
// Cookies do not work with iframes due to SameSite=Strict, so instead, if we
// detect that the application is being viewed through an iframe, use
// localStorage to store the `anon_session_id` and pass it as a header in the
// when making calls to weave-python with remoteHttp.ts
const anonSessionStorage = {
  has(): boolean {
    const inIframe = isInIframe();
    if (inIframe) {
      return safeLocalStorage.getItem(ANON_SESSION_KEY) !== null;
    } else {
      return getCookieBool(ANON_SESSION_KEY);
    }
  },
  set(sessionId: string): void {
    const inIframe = isInIframe();
    if (inIframe) {
      safeLocalStorage.setItem(ANON_SESSION_KEY, sessionId);
    } else {
      setCookie(ANON_SESSION_KEY, sessionId);
    }
  },

  remove(): void {
    const inIframe = isInIframe();
    if (inIframe) {
      safeLocalStorage.removeItem(ANON_SESSION_KEY);
    } else {
      unsetCookie(ANON_SESSION_KEY);
    }
  },
};

export const useAnonUserSession = (viewer: Viewer | undefined) => {
  useEffect(() => {
    if (viewer == null) {
      // Create session for anonymous user if it doesn't exist
      if (!anonSessionStorage.has()) {
        const sessionId = 'anon-' + generateSessionId();
        anonSessionStorage.set(sessionId);
      }
    } else {
      // Clear session when user logs in
      if (anonSessionStorage.has()) {
        anonSessionStorage.remove();
      }
    }
  }, [viewer]);
};
